import { type BaseComponentProps } from "../../types/puck";
import { cn } from "../../lib/utils";

export interface ProcessStep {
  step: number;
  title: string;
  description: string;
}

export interface ProcessStepsProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  steps: ProcessStep[];
  layout: "vertical" | "horizontal" | "numbered";
  accentColor?: string;
  backgroundColor?: string;
}

export const ProcessSteps: React.FC<ProcessStepsProps> = ({
  heading,
  subheading,
  steps,
  layout = "vertical",
  accentColor,
  backgroundColor,
  id,
  className,
}) => {
  const surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const;

  const renderVerticalLayout = () => (
    <div className="jobsite:relative">
      {/* Vertical line connector */}
      <div className="jobsite:absolute jobsite:left-6 jobsite:top-0 jobsite:bottom-0 jobsite:w-0.5 jobsite:bg-border jobsite:hidden jobsite:md:block" />

      <div className="jobsite:space-y-8">
        {steps.map((step, idx) => (
          <div key={idx} className="jobsite:relative jobsite:flex jobsite:gap-6">
            {/* Step number circle */}
            <div
              className={cn(
                "jobsite:relative jobsite:z-10 jobsite:flex jobsite:items-center jobsite:justify-center",
                "jobsite:w-12 jobsite:h-12 jobsite:rounded-full jobsite:font-bold jobsite:text-lg",
                "jobsite:bg-primary jobsite:text-primary-foreground jobsite:shrink-0",
              )}
              style={accentColor ? { backgroundColor: accentColor } : undefined}
            >
              {step.step}
            </div>

            {/* Content */}
            <div className="jobsite:pt-1">
              <h3 className="jobsite:text-xl jobsite:mb-2 jobsite:font-heading jobsite:text-foreground">
                {step.title}
              </h3>
              <p className="jobsite:text-base jobsite:text-muted-foreground jobsite:leading-relaxed">
                {step.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderHorizontalLayout = () => (
    <div className="jobsite:relative">
      {/* Single connector line behind all circles — hidden below lg or for single steps */}
      {steps.length > 1 && (
        <div
          className="jobsite:hidden jobsite:lg:block jobsite:absolute jobsite:h-0.5 jobsite:bg-border"
          style={{
            top: "1.5rem",
            left: `calc(${100 / steps.length / 2}% + 1.5rem)`,
            right: `calc(${100 / steps.length / 2}% + 1.5rem)`,
          }}
        />
      )}

      <div className="jobsite:grid jobsite:grid-cols-1 jobsite:sm:grid-cols-2 jobsite:lg:grid-cols-3 jobsite:gap-10 jobsite:lg:gap-16">
        {steps.map((step, idx) => (
          <div key={idx} className="jobsite:relative jobsite:text-center jobsite:group">
            {/* Step number - minimal circle */}
            <div
              className={cn(
                "jobsite:relative jobsite:z-10 jobsite:mx-auto jobsite:flex jobsite:items-center jobsite:justify-center",
                "jobsite:w-12 jobsite:h-12 jobsite:rounded-full jobsite:font-semibold jobsite:text-base jobsite:mb-5",
                "jobsite:border-2 jobsite:border-primary jobsite:text-primary jobsite:bg-background",
                "jobsite:transition-all jobsite:duration-300",
                "jobsite:group-hover:bg-primary jobsite:group-hover:text-primary-foreground",
              )}
              style={accentColor ? { borderColor: accentColor, color: accentColor } : undefined}
            >
              {step.step}
            </div>

            {/* Content */}
            <h3 className="jobsite:text-base jobsite:mb-2 jobsite:font-heading jobsite:text-foreground">
              {step.title}
            </h3>
            <p className="jobsite:text-sm jobsite:text-muted-foreground jobsite:leading-relaxed jobsite:max-w-xs jobsite:mx-auto">
              {step.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderNumberedLayout = () => (
    <div className="jobsite:space-y-6">
      {steps.map((step, idx) => (
        <div
          key={idx}
          className={cn(
            "jobsite:flex jobsite:gap-4 jobsite:p-6",
            "jobsite:bg-background jobsite:border jobsite:border-border/70",
          )}
          style={surfaceRadiusStyle}
        >
          {/* Large step number */}
          <div
            className={cn(
              "jobsite:text-4xl jobsite:font-bold jobsite:text-primary jobsite:shrink-0 jobsite:w-12",
            )}
            style={accentColor ? { color: accentColor } : undefined}
          >
            {String(step.step).padStart(2, "0")}
          </div>

          {/* Content */}
          <div>
            <h3 className="jobsite:text-xl jobsite:mb-2 jobsite:font-heading jobsite:text-card-foreground">
              {step.title}
            </h3>
            <p className="jobsite:text-base jobsite:text-muted-foreground jobsite:leading-relaxed">
              {step.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <section
      id={id}
      className={cn(
        "jobsite:py-16 jobsite:md:py-24",
        backgroundColor ? "" : "jobsite:bg-background",
        className,
      )}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        {/* Header */}
        <div className="jobsite:text-center jobsite:mb-12 jobsite:max-w-3xl jobsite:mx-auto">
          <h2 className="jobsite:text-3xl jobsite:sm:text-4xl jobsite:mb-4 jobsite:font-heading jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground">
            {heading}
          </h2>
          {subheading && (
            <p className="jobsite:text-lg jobsite:text-pretty jobsite:text-muted-foreground">{subheading}</p>
          )}
        </div>

        {/* Steps */}
        <div className="jobsite:max-w-4xl jobsite:mx-auto">
          {layout === "vertical" && renderVerticalLayout()}
          {layout === "horizontal" && renderHorizontalLayout()}
          {layout === "numbered" && renderNumberedLayout()}
        </div>
      </div>
    </section>
  );
};
